import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import RouteMap from "../components/RouteMap";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapPin, ArrowRight, AlertTriangle, Calculator, ChevronDown, Search, X, Loader2 } from "lucide-react";

// ── Recherche d'adresse via Nominatim (OpenStreetMap) ────────────
async function searchNominatim(query) {
  if (!query || query.length < 3) return [];
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6&countrycodes=fr,be,es,de&accept-language=fr`;
  const res = await fetch(url, { headers: { "User-Agent": "DepannRoute/1.0" } });
  if (!res.ok) return [];
  const data = await res.json();
  return data.map(r => ({
    label: r.display_name.split(",").slice(0, 3).join(", "),
    city:  r.address?.city || r.address?.town || r.address?.village || r.address?.county || r.display_name.split(",")[0],
    country: (r.address?.country_code || "fr").toUpperCase(),
    lat: parseFloat(r.lat),
    lng: parseFloat(r.lon),
  }));
}

function LocationSearch({ label, icon: Icon, value, onChange, placeholder, testId }) {
  const [query,       setQuery]       = useState(value?.label || "");
  const [results,     setResults]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [open,        setOpen]        = useState(false);
  const debounceRef   = useRef(null);
  const wrapRef       = useRef(null);

  // Ferme le dropdown si clic hors du composant
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    onChange(null); // reset jusqu'à sélection
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 3) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const res = await searchNominatim(q);
      setResults(res);
      setOpen(res.length > 0);
      setLoading(false);
    }, 350);
  };

  const select = (item) => {
    setQuery(item.label);
    onChange(item);
    setOpen(false);
    setResults([]);
  };

  const clear = () => {
    setQuery("");
    onChange(null);
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Label className="flex items-center gap-1 mb-1">
        <Icon className="h-3.5 w-3.5 text-orange-600" /> {label}
      </Label>
      <div className="relative">
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          className={`w-full border rounded-lg px-3 py-2.5 pr-8 text-sm focus:outline-none transition-colors ${
            value ? "border-orange-400 bg-orange-50/30" : "border-slate-200 focus:border-orange-400"
          }`}
        />
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          ) : value ? (
            <button type="button" onClick={clear} className="text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <Search className="w-4 h-4 text-slate-300" />
          )}
        </div>
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {results.map((item, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={() => select(item)}
              className="w-full text-left px-4 py-2.5 hover:bg-orange-50 transition-colors border-b border-slate-100 last:border-0"
            >
              <div className="text-sm font-medium text-slate-900 truncate">{item.city}</div>
              <div className="text-xs text-slate-400 truncate">{item.label}</div>
            </button>
          ))}
        </div>
      )}
      {value && (
        <div className="mt-1 text-[11px] text-emerald-600 font-medium flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {value.city} · {value.lat.toFixed(4)}, {value.lng.toFixed(4)}
        </div>
      )}
    </div>
  );
}

// ── Haversine (fallback si OSRM indisponible) ────────────────────
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371, toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

// ── OSRM routing (distance routière réelle + durée + péages) ─────
async function fetchOsrmRoute(fromLng, fromLat, toLng, toLat) {
  const url = `https://router.project-osrm.org/route/v1/driving/${fromLng},${fromLat};${toLng},${toLat}?steps=true&overview=simplified`;
  const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
  if (!res.ok) throw new Error("OSRM error");
  const data = await res.json();
  if (data.code !== "Ok") throw new Error("OSRM no route");
  const leg = data.routes[0].legs[0];

  // Calcul du % d'autoroute via les refs des steps (A 6, A 7, A 9…)
  let autorouteM = 0;
  const totalM = leg.distance;
  (leg.steps || []).forEach(step => {
    const ref = step.ref || "";
    if (/\bA\s*\d+\b/.test(ref)) autorouteM += step.distance;
  });
  const autoRoutePct = totalM > 0 ? autorouteM / totalM : 0;

  // Estimation péages : 0–5% autoroute → 0€, 5–30% → 5€, 30–60% → 15€, 60–80% → 22€, 80%+ → 30€
  let tollMin = 0, tollMax = 0;
  if (autoRoutePct >= 0.80)      { tollMin = 25; tollMax = 30; }
  else if (autoRoutePct >= 0.60) { tollMin = 18; tollMax = 25; }
  else if (autoRoutePct >= 0.30) { tollMin = 10; tollMax = 18; }
  else if (autoRoutePct >= 0.05) { tollMin = 5;  tollMax = 10; }

  return {
    distKm:      Math.round(totalM / 1000),
    durationMin: Math.round(leg.duration / 60),
    autoRoutePct: Math.round(autoRoutePct * 100),
    tollMin,
    tollMax,
  };
}

// ── Constantes du modèle de coût pro ─────────────────────────────
// Valeurs de référence marché France 2026 (plateau dépannage individuel)
const COST_BASE = {
  vitesseAutoroute:    110,   // km/h vitesse effective avec chargement
  vitesseNationale:     70,
  consoAutorouteL100:   25,   // L/100 km (plateau chargé)
  consoNationaleL100:   28,
  prixCarburantLitre:  1.85,  // € diesel moyen 2026
  tempsOperationsH:    1.25,  // h (chargement + déchargement + paperasse)
  forfaitMission:        60,  // € (déplacement fixe, admin)
  coutHoraire:           35,  // €/h conducteur + charges
  coutKmUsure:         0.18,  // €/km (amortissement véhicule)
  margePct:              15,  // % marge pro
};

const SERVICE_CFG = {
  car:        ["plateau", "groupe_ouvert", "groupe_ferme"],
  suv:        ["plateau", "groupe_ouvert", "groupe_ferme"],
  van:        ["plateau", "groupe_ouvert"],
  motorcycle: ["plateau"],
  truck:      ["plateau"],
};
const SERVICE_LABEL  = { plateau: "Plateau individuel", groupe_ouvert: "Groupé ouvert (3–7 j)", groupe_ferme: "Groupé fermé (luxe)" };
const COEFF_URGENCE  = { low: 0.90, medium: 1.0, high: 1.25, emergency: 1.50 };
const URG_NOTE       = { low: "−10% anticipation", high: "+25% urgence", emergency: "+50% urgence extrême" };
const COEFF_GROUPE   = { plateau: 1.0, groupe_ouvert: 0.55, groupe_ferme: 1.40 };

function calcTransport({ kmAutoroute, kmNationale, peageTotal, urgency, service }) {
  const coeffUrgence = COEFF_URGENCE[urgency] ?? 1;
  const coeffGroupe  = COEFF_GROUPE[service]  ?? 1;
  const b = COST_BASE;

  const tempsRouteH = (kmAutoroute / b.vitesseAutoroute) + (kmNationale / b.vitesseNationale);
  const tempsTotalH = tempsRouteH + b.tempsOperationsH;
  const litres      = (kmAutoroute * b.consoAutorouteL100 / 100) + (kmNationale * b.consoNationaleL100 / 100);
  const kmTotal     = kmAutoroute + kmNationale;

  const coutCarburant = litres * b.prixCarburantLitre;
  const coutTemps     = tempsTotalH * b.coutHoraire;
  const coutUsure     = kmTotal * b.coutKmUsure;

  let sousTotal = (b.forfaitMission + coutTemps + coutCarburant + peageTotal + coutUsure)
    * coeffUrgence * coeffGroupe;

  const marge   = sousTotal * (b.margePct / 100);
  const totalHT = Math.round(sousTotal + marge);

  return { kmTotal, tempsRouteH, tempsTotalH, litres, coutCarburant, coutTemps, coutUsure, peage: peageTotal, sousTotal, marge, totalHT };
}

// ── Boîte d'estimation ───────────────────────────────────────────
function EstimateBox({ km, vehicleType, urgency, budget, durationMin, autoRoutePct, tollMin, tollMax }) {
  const [open,    setOpen]    = useState(true);
  const [service, setService] = useState("plateau");

  const est = useMemo(() => {
    if (!km || km < 1) return null;
    const pct          = (autoRoutePct ?? 0) / 100;
    const kmAutoroute  = Math.round(km * pct);
    const kmNationale  = km - kmAutoroute;
    const peageMed     = (tollMin + tollMax) / 2;

    const med  = calcTransport({ kmAutoroute, kmNationale, peageTotal: peageMed, urgency, service });
    // fourchette ±12% autour du point médian
    return {
      med:   med.totalHT,
      min:   Math.round(med.totalHT * 0.88),
      max:   Math.round(med.totalHT * 1.12),
      detail: med,
      km,
      service,
      urgCoeff: COEFF_URGENCE[urgency] ?? 1,
      urgNote:  URG_NOTE[urgency],
      hasToll:  tollMin > 0,
      availableServices: SERVICE_CFG[vehicleType] || ["plateau"],
    };
  }, [km, vehicleType, urgency, service, autoRoutePct, tollMin, tollMax]);

  if (!est) return null;

  const budgetN = parseFloat(budget) || 0;
  const diff    = budgetN > 0 ? Math.round((budgetN - est.med) / est.med * 100) : null;
  const d       = est.detail;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button type="button" className="w-full flex items-center justify-between px-4 py-3" onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
            <Calculator className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="text-left">
            <div className="text-xs font-bold text-slate-900">Estimation coût réel 2026</div>
            <div className="text-[11px] text-slate-500">
              {SERVICE_LABEL[service]} · {est.km} km
              {durationMin ? ` · ${Math.floor(durationMin/60)}h${String(durationMin%60).padStart(2,"0")}` : ""}
              {est.hasToll ? " · péages inclus" : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="font-display font-extrabold text-xl text-orange-600">{est.med} €</div>
            <div className="text-[10px] text-slate-400">{est.min}–{est.max} €</div>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-3">
          {/* Sélecteur service */}
          {est.availableServices.length > 1 && (
            <div className="flex gap-1.5 flex-wrap">
              {est.availableServices.map(s => (
                <button key={s} type="button" onClick={() => setService(s)}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                    service === s ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
                  }`}>
                  {SERVICE_LABEL[s]}
                </button>
              ))}
            </div>
          )}

          {/* Décomposition des coûts */}
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-500">
              <span>Forfait mission (déplacement + admin)</span>
              <span className="font-medium text-slate-700">{Math.round(COST_BASE.forfaitMission)} €</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Temps conducteur ({d.tempsTotalH.toFixed(1)} h × {COST_BASE.coutHoraire} €/h)</span>
              <span className="font-medium text-slate-700">{Math.round(d.coutTemps)} €</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Carburant ({d.litres.toFixed(0)} L × {COST_BASE.prixCarburantLitre} €/L)</span>
              <span className="font-medium text-slate-700">{Math.round(d.coutCarburant)} €</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Usure véhicule ({est.km} km × {COST_BASE.coutKmUsure} €/km)</span>
              <span className="font-medium text-slate-700">{Math.round(d.coutUsure)} €</span>
            </div>
            {est.hasToll && (
              <div className="flex justify-between text-slate-500">
                <span className="flex items-center gap-1">
                  Péages estimés
                  {autoRoutePct != null && <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-full">{autoRoutePct}% autoroute</span>}
                </span>
                <span className="font-medium text-amber-600">{tollMin}–{tollMax} €</span>
              </div>
            )}
            {est.urgCoeff !== 1.0 && (
              <div className="flex justify-between text-slate-500">
                <span>{est.urgNote}</span>
                <span className={`font-medium ${est.urgCoeff > 1 ? "text-amber-600" : "text-emerald-600"}`}>×{est.urgCoeff}</span>
              </div>
            )}
            {service !== "plateau" && (
              <div className="flex justify-between text-slate-500">
                <span>{service === "groupe_ouvert" ? "Abattement groupé ouvert" : "Surcoût fermé (luxe)"}</span>
                <span className={`font-medium ${service === "groupe_ouvert" ? "text-emerald-600" : "text-amber-600"}`}>
                  ×{COEFF_GROUPE[service]}
                </span>
              </div>
            )}
            <div className="flex justify-between text-slate-500">
              <span>Marge pro ({COST_BASE.margePct}%)</span>
              <span className="font-medium text-slate-700">{Math.round(d.marge)} €</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-amber-200 font-bold">
              <span className="text-slate-900">Prix estimé HT</span>
              <span className="text-orange-600 font-display text-base">{est.min}–{est.max} €</span>
            </div>
          </div>

          {/* Astuce groupé */}
          {service === "plateau" && est.availableServices.includes("groupe_ouvert") && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
              💡 En groupé ouvert (délai 3–7 j) : économie ~45% → <strong>{Math.round(est.min * 0.55)}–{Math.round(est.max * 0.60)} €</strong>
            </div>
          )}
        </div>
      )}

      {/* Comparaison budget */}
      {diff !== null && (
        <div className={`mx-4 mb-3 flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg ${
          diff >= 0 ? "bg-emerald-100 text-emerald-700" : diff >= -20 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
        }`}>
          {diff >= 0
            ? `✓ Budget ${diff}% au-dessus de l'estimation — offres rapides`
            : diff >= -20
            ? `⚠ Budget ${Math.abs(diff)}% sous l'estimation — peu de pros disponibles`
            : `✗ Budget ${Math.abs(diff)}% sous l'estimation — très peu de réponses attendues`
          }
        </div>
      )}
    </div>
  );
}

// ── Constantes ────────────────────────────────────────────────────
const urgencies = [
  { v: "low",       label: "Basse"   },
  { v: "medium",    label: "Moyenne" },
  { v: "high",      label: "Haute"   },
  { v: "emergency", label: "URGENCE" },
];
const vehicles = [
  { v: "car",        label: "Voiture (VL / Berline)" },
  { v: "suv",        label: "SUV / Monospace"        },
  { v: "van",        label: "Utilitaire ≤3,5t"       },
  { v: "motorcycle", label: "Moto / Scooter"         },
  { v: "truck",      label: "Poids lourd"            },
];

export default function NewRequest() {
  // Préremplissage depuis la vitrine des trajets retour (/trajets-disponibles)
  const [searchParams] = useSearchParams();
  const paramLoc = (prefix) => {
    const city = searchParams.get(`${prefix}_city`);
    const lat = parseFloat(searchParams.get(`${prefix}_lat`));
    const lng = parseFloat(searchParams.get(`${prefix}_lng`));
    if (!city || Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { label: city, city, country: searchParams.get(`${prefix}_country`) || "FR", lat, lng };
  };
  const [pickup,  setPickup]  = useState(() => paramLoc("pickup")); // { city, country, lat, lng, label }
  const [dropoff, setDropoff] = useState(() => paramLoc("dropoff"));
  const [title,        setTitle]        = useState("");
  const [description,  setDescription]  = useState("");
  const [vehicleType,  setVehicleType]  = useState("car");
  const [vehicleBrand, setVehicleBrand] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [urgency,      setUrgency]      = useState("medium");
  const [pickupDate,   setPickupDate]   = useState(() => {
    const fromTrip = searchParams.get("date");
    if (fromTrip && /^\d{4}-\d{2}-\d{2}$/.test(fromTrip)) return `${fromTrip}T10:00`;
    const d = new Date(); d.setHours(d.getHours() + 2, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [budget,  setBudget]  = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
  }, []);

  const routes = pickup && dropoff ? [{ pickup, dropoff }] : [];

  const [routeData,    setRouteData]    = useState(null); // { distKm, durationMin, autoRoutePct, tollMin, tollMax }
  const [routeLoading, setRouteLoading] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!pickup || !dropoff) { setRouteData(null); return; }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRouteLoading(true);
    fetchOsrmRoute(pickup.lng, pickup.lat, dropoff.lng, dropoff.lat)
      .then(data => { if (!controller.signal.aborted) { setRouteData(data); setRouteLoading(false); } })
      .catch(() => {
        if (!controller.signal.aborted) {
          // fallback haversine
          setRouteData({ distKm: haversine(pickup.lat, pickup.lng, dropoff.lat, dropoff.lng), durationMin: null });
          setRouteLoading(false);
        }
      });
    return () => controller.abort();
  }, [pickup?.city, dropoff?.city]);

  const distKm       = routeData?.distKm       ?? null;
  const durationMin  = routeData?.durationMin  ?? null;
  const autoRoutePct = routeData?.autoRoutePct ?? null;
  const tollMin      = routeData?.tollMin      ?? 0;
  const tollMax      = routeData?.tollMax      ?? 0;


  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pickup || !dropoff) { setError("Veuillez sélectionner ville de départ et d'arrivée."); return; }
    if (pickup.city === dropoff.city) { setError("Les villes de départ et d'arrivée doivent être différentes."); return; }
    setLoading(true);
    try {
      const payload = {
        title, description,
        vehicle_type:  vehicleType,
        vehicle_brand: vehicleBrand || null,
        vehicle_model: vehicleModel || null,
        urgency,
        pickup_date: new Date(pickupDate).toISOString(),
        budget_eur:  budget ? parseFloat(budget) : null,
        pickup:  { city: pickup.city,  country: pickup.country,  lat: pickup.lat,  lng: pickup.lng  },
        dropoff: { city: dropoff.city, country: dropoff.country, lat: dropoff.lat, lng: dropoff.lng },
        distance_km:  distKm  || haversine(pickup.lat, pickup.lng, dropoff.lat, dropoff.lng),
        duration_min: durationMin || null,
      };
      const { data } = await api.post("/requests", payload);
      navigate(`/requests/${data.request_id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="new-request-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="text-[11px] uppercase tracking-widest font-bold text-orange-600">Nouvelle demande</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight">Publier une mission</h1>
          <p className="text-sm text-slate-600 mt-1">Renseignez votre trajet et les pros certifiés vous enverront leurs offres.</p>

          <div className="grid lg:grid-cols-5 gap-6 mt-6">

            {/* ── Formulaire ── */}
            <form
              onSubmit={submit}
              className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-6 space-y-4"
              data-testid="new-request-form"
            >
              <div>
                <Label htmlFor="title">Titre de la mission *</Label>
                <Input
                  id="title" required
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Ex : Remorquage Peugeot 308 en panne A6"
                  data-testid="new-req-title"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <LocationSearch
                  label="Départ *"
                  icon={MapPin}
                  value={pickup}
                  onChange={setPickup}
                  placeholder="Ville, adresse, code postal…"
                  testId="pickup-city"
                />
                <LocationSearch
                  label="Arrivée *"
                  icon={ArrowRight}
                  value={dropoff}
                  onChange={setDropoff}
                  placeholder="Ville, adresse, code postal…"
                  testId="dropoff-city"
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Type de véhicule</Label>
                  <Select value={vehicleType} onValueChange={setVehicleType}>
                    <SelectTrigger data-testid="vehicle-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {vehicles.map(v => <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Marque</Label>
                  <Input value={vehicleBrand} onChange={e => setVehicleBrand(e.target.value)} placeholder="Peugeot" />
                </div>
                <div>
                  <Label>Modèle</Label>
                  <Input value={vehicleModel} onChange={e => setVehicleModel(e.target.value)} placeholder="308" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Urgence</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger data-testid="urgency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {urgencies.map(u => <SelectItem key={u.v} value={u.v}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date d'enlèvement *</Label>
                  <Input
                    type="datetime-local" required
                    value={pickupDate}
                    onChange={e => setPickupDate(e.target.value)}
                    data-testid="pickup-date"
                  />
                </div>
              </div>

              <div>
                <Label>Budget souhaité (€, optionnel)</Label>
                <Input
                  type="number" min="0"
                  value={budget}
                  onChange={e => setBudget(e.target.value)}
                  placeholder="350"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Panne moteur, véhicule immobilisé sur la bande d'arrêt d'urgence…"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12 text-base font-display font-bold"
                data-testid="new-req-submit"
              >
                {loading ? "Publication…" : "Publier la mission"}
              </Button>
            </form>

            {/* ── Colonne droite ── */}
            <aside className="lg:col-span-2 space-y-4">

              {/* Estimation marché — apparaît dès que départ + arrivée + véhicule sont renseignés */}
              {distKm ? (
                <EstimateBox km={distKm} vehicleType={vehicleType} urgency={urgency} budget={budget} durationMin={durationMin} autoRoutePct={autoRoutePct} tollMin={tollMin} tollMax={tollMax} />
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center mx-auto mb-2">
                    <Calculator className="w-5 h-5 text-orange-500" />
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Sélectionnez un départ, une arrivée et un type de véhicule pour obtenir une <strong className="text-slate-700">estimation du prix marché</strong>.
                  </p>
                </div>
              )}

              {/* Carte */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Aperçu du trajet</div>
                <RouteMap routes={routes} height={260} />
                {pickup && dropoff && (
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-slate-700">
                      <span className="font-semibold">{pickup.city}</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                      <span className="font-semibold">{dropoff.city}</span>
                    </div>
                    {routeLoading && (
                      <span className="text-xs text-slate-400 animate-pulse">Calcul itinéraire…</span>
                    )}
                    {!routeLoading && distKm && (
                      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                        {distKm} km{durationMin ? ` · ${Math.floor(durationMin / 60)}h${String(durationMin % 60).padStart(2, "0")}` : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-slate-900 text-white rounded-xl p-4 text-xs">
                <div className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">Zéro trajet à vide</div>
                <div className="mt-1 text-slate-300 leading-relaxed">
                  Dès publication, notre moteur propose votre trajet aux dépanneurs dont l'axe et
                  l'horaire correspondent. Vous recevez plusieurs offres en temps réel.
                </div>
              </div>
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}
